/**
 * ChatMessageMenu — context menu for long-press on messages.
 * Copy text, edit own text, delete own messages. iOS-style action sheet.
 */

import React from 'react'
import { Copy, Pencil, Trash2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'

// ── Types ──────────────────────────────────────────────────

export interface MessageMenuState {
  messageId: string
  text: string | null
  isMine: boolean
}

interface ChatMessageMenuProps {
  state: MessageMenuState | null
  onClose: () => void
  onCopy: (text: string) => void
  onEdit: (messageId: string, currentText: string) => void
  onDelete: (messageId: string) => void
}

// ── Component ──────────────────────────────────────────────

export const ChatMessageMenu: React.FC<ChatMessageMenuProps> = ({
  state,
  onClose,
  onCopy,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation()
  const isOpen = state !== null

  const handleCopy = (): void => {
    if (!state?.text) return
    hapticImpact('light')
    onCopy(state.text)
    onClose()
  }

  const handleEdit = (): void => {
    if (!state?.text) return
    hapticImpact('light')
    onEdit(state.messageId, state.text)
    onClose()
  }

  const handleDelete = (): void => {
    if (!state) return
    hapticImpact('medium')
    onDelete(state.messageId)
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]"
            onClick={onClose}
          />

          {/* Menu */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-0 left-0 right-0 z-50 px-3"
            style={{ paddingBottom: 'max(16px, calc(var(--tg-safe-area-inset-bottom, 0px) + 12px))' }}
          >
            <div className="max-w-lg mx-auto rounded-2xl overflow-hidden
              bg-surface/95 backdrop-blur-xl border border-white/[0.06]">

              {/* Copy */}
              {state?.text && (
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-3 w-full px-4 py-3.5
                    hover:bg-white/[0.04] active:bg-white/[0.06]
                    transition-colors tap-highlight-transparent"
                >
                  <Copy size={18} className="text-text-hint" />
                  <span className="text-[15px] text-text">{t('dm.copy')}</span>
                </button>
              )}

              {/* Edit (only own messages with text) */}
              {state?.isMine && state.text && (
                <>
                  <div className="h-px bg-white/[0.06] mx-4" />
                  <button
                    onClick={handleEdit}
                    className="flex items-center gap-3 w-full px-4 py-3.5
                      hover:bg-white/[0.04] active:bg-white/[0.06]
                      transition-colors tap-highlight-transparent"
                  >
                    <Pencil size={18} className="text-text-hint" />
                    <span className="text-[15px] text-text">{t('dm.edit')}</span>
                  </button>
                </>
              )}

              {/* Delete (only own messages) */}
              {state?.isMine && (
                <>
                  <div className="h-px bg-white/[0.06] mx-4" />
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-3 w-full px-4 py-3.5
                      hover:bg-white/[0.04] active:bg-white/[0.06]
                      transition-colors tap-highlight-transparent"
                  >
                    <Trash2 size={18} className="text-danger" />
                    <span className="text-[15px] text-danger">{t('dm.delete')}</span>
                  </button>
                </>
              )}
            </div>

            {/* Cancel */}
            <button
              onClick={onClose}
              className="w-full mt-2 py-3.5 rounded-2xl text-center
                bg-surface/95 backdrop-blur-xl border border-white/[0.06]
                text-[15px] font-medium text-text
                hover:bg-white/[0.04] active:bg-white/[0.06]
                transition-colors tap-highlight-transparent"
            >
              {t('dm.cancel')}
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
